import { useCallback, useEffect, useRef, useState } from "react";
import { Bold, Code, Eye, Italic, Quote, Strikethrough, Underline } from "lucide-react";

// Barre de mise en forme façon Discord : elle apparaît au-dessus du texte sélectionné
// dans un champ (message, embed…) et ajoute ou retire le Markdown de Discord :
// **gras**, *italique*, __souligné__, ~~barré~~, > citation, `code`, ||spoiler||.
// Raccourcis : Ctrl+B, Ctrl+I, Ctrl+U (Cmd sur Mac).
// Le parent du champ doit être en position relative.

const MIRROR_PROPS = [
  "boxSizing", "width", "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth",
  "paddingTop", "paddingRight", "paddingBottom", "paddingLeft", "fontStyle", "fontVariant", "fontWeight",
  "fontStretch", "fontSize", "lineHeight", "fontFamily", "textAlign", "textTransform", "textIndent",
  "letterSpacing", "wordSpacing", "tabSize",
];

// Position (dans le champ) du caractère à l'index pos, via une copie invisible du champ.
function caretXY(el, pos) {
  const cs = window.getComputedStyle(el);
  const div = document.createElement("div");
  for (const p of MIRROR_PROPS) div.style[p] = cs[p];
  Object.assign(div.style, { position: "absolute", visibility: "hidden", top: "0", left: "-9999px", whiteSpace: "pre-wrap", wordWrap: "break-word", overflow: "hidden" });
  div.textContent = el.value.slice(0, pos);
  const span = document.createElement("span");
  span.textContent = el.value.slice(pos) || ".";
  div.appendChild(span);
  document.body.appendChild(div);
  const out = { top: span.offsetTop - el.scrollTop, left: span.offsetLeft - el.scrollLeft };
  div.remove();
  return out;
}

// Nombre de caractères ch à la suite, avant (dir -1) ou après (dir 1) l'index i.
function run(text, i, dir, ch) {
  let n = 0;
  for (let j = dir < 0 ? i - 1 : i; j >= 0 && j < text.length && text[j] === ch; j += dir) n += 1;
  return n;
}

// Le run de ch autour couvre-t-il exactement ce marqueur ? (* contre **, _ contre __)
function covers(n, len) {
  return len === 1 ? n % 2 === 1 : n === 2 || n === 3;
}

export function toggleWrap(text, start, end, mark) {
  const len = mark.length;
  const ch = mark[0];
  const sel = text.slice(start, end);
  const same = mark === ch.repeat(len);
  const outside = text.slice(start - len, start) === mark && text.slice(end, end + len) === mark
    && (!same || (covers(run(text, start, -1, ch), len) && covers(run(text, end, 1, ch), len)));
  if (outside) {
    return { text: text.slice(0, start - len) + sel + text.slice(end + len), start: start - len, end: end - len };
  }
  if (sel.length > 2 * len && sel.startsWith(mark) && sel.endsWith(mark)) {
    const inner = sel.slice(len, -len);
    return { text: text.slice(0, start) + inner + text.slice(end), start, end: start + inner.length };
  }
  return { text: text.slice(0, start) + mark + sel + mark + text.slice(end), start: start + len, end: end + len };
}

export function toggleQuote(text, start, end) {
  const from = text.lastIndexOf("\n", start - 1) + 1;
  const nl = text.indexOf("\n", end > start && text[end - 1] === "\n" ? end - 1 : end);
  const to = nl === -1 ? text.length : nl;
  const lines = text.slice(from, to).split("\n");
  const quoted = lines.every((l) => l.startsWith("> "));
  const block = (quoted ? lines.map((l) => l.slice(2)) : lines.map((l) => `> ${l}`)).join("\n");
  return { text: text.slice(0, from) + block + text.slice(to), start: from, end: from + block.length };
}

export function toggleCode(text, start, end) {
  const sel = text.slice(start, end);
  if (!sel.includes("\n")) return toggleWrap(text, start, end, "`");
  if (/^```[^\n]*\n[\s\S]*\n```$/.test(sel)) {
    const inner = sel.replace(/^```[^\n]*\n/, "").replace(/\n```$/, "");
    return { text: text.slice(0, start) + inner + text.slice(end), start, end: start + inner.length };
  }
  const block = "```\n" + sel + "\n```";
  return { text: text.slice(0, start) + block + text.slice(end), start: start + 4, end: start + 4 + sel.length };
}

const TOOLS = [
  { id: "bold", icon: Bold, label: "Gras (Ctrl+B)", apply: (t, s, e) => toggleWrap(t, s, e, "**") },
  { id: "italic", icon: Italic, label: "Italique (Ctrl+I)", apply: (t, s, e) => toggleWrap(t, s, e, "*") },
  { id: "underline", icon: Underline, label: "Souligné (Ctrl+U)", apply: (t, s, e) => toggleWrap(t, s, e, "__") },
  { id: "strike", icon: Strikethrough, label: "Barré", apply: (t, s, e) => toggleWrap(t, s, e, "~~") },
  { sep: true },
  { id: "quote", icon: Quote, label: "Citation", apply: toggleQuote },
  { id: "code", icon: Code, label: "Code", apply: toggleCode },
  { id: "spoiler", icon: Eye, label: "Spoiler", apply: (t, s, e) => toggleWrap(t, s, e, "||") },
];
const SHORTCUTS = { b: "bold", i: "italic", u: "underline" };

export default function DiscordFormatBar({ inputRef, value, onChange, disabled, maxLength }) {
  const [pos, setPos] = useState(null);
  const barRef = useRef(null);

  const place = useCallback(() => {
    const el = inputRef.current;
    if (!el || disabled || document.activeElement !== el || el.selectionStart === el.selectionEnd) {
      setPos(null);
      return;
    }
    const a = caretXY(el, el.selectionStart);
    const b = caretXY(el, el.selectionEnd);
    const sameLine = Math.abs(a.top - b.top) < 4;
    const width = barRef.current?.offsetWidth || 260;
    const x = el.offsetLeft + (sameLine ? (a.left + b.left) / 2 : a.left);
    const parentWidth = el.offsetParent?.clientWidth || el.offsetWidth;
    const left = Math.max(0, Math.min(x - width / 2, parentWidth - width));
    const top = el.offsetTop + Math.max(0, a.top) - 40;
    setPos({ left, top });
  }, [inputRef, disabled]);

  const apply = useCallback((id) => {
    const el = inputRef.current;
    const tool = TOOLS.find((t) => t.id === id);
    if (!el || !tool || disabled) return;
    const res = tool.apply(el.value, el.selectionStart, el.selectionEnd);
    if (maxLength && res.text.length > maxLength) return;
    onChange(res.text);
    // Après le rendu : on garde le même texte sélectionné, comme sur Discord.
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(res.start, res.end);
      place();
    }, 0);
  }, [inputRef, disabled, maxLength, onChange, place]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return undefined;
    const onKey = (e) => {
      const id = (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && SHORTCUTS[e.key.toLowerCase()];
      if (id && el.selectionStart !== el.selectionEnd) {
        e.preventDefault();
        apply(id);
      }
    };
    const hide = () => setPos(null);
    el.addEventListener("keydown", onKey);
    el.addEventListener("select", place);
    el.addEventListener("mouseup", place);
    el.addEventListener("keyup", place);
    el.addEventListener("scroll", place);
    el.addEventListener("blur", hide);
    document.addEventListener("selectionchange", place);
    return () => {
      el.removeEventListener("keydown", onKey);
      el.removeEventListener("select", place);
      el.removeEventListener("mouseup", place);
      el.removeEventListener("keyup", place);
      el.removeEventListener("scroll", place);
      el.removeEventListener("blur", hide);
      document.removeEventListener("selectionchange", place);
    };
  }, [inputRef, apply, place]);

  // Texte modifié de l'extérieur : la barre suit la sélection.
  useEffect(() => { if (pos) place(); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!pos) return null;
  return (
    <div ref={barRef} role="toolbar" aria-label="Mise en forme" data-testid="format-bar"
      className="absolute z-30 flex items-center gap-0.5 rounded-lg border border-black/40 bg-[#111214] p-1 shadow-xl"
      style={{ left: pos.left, top: pos.top }} onMouseDown={(e) => e.preventDefault()}>
      {TOOLS.map((t, i) => t.sep
        ? <span key={`sep-${i}`} className="mx-1 h-5 w-px bg-white/15" />
        : (
          <button key={t.id} type="button" title={t.label} aria-label={t.label} data-testid={`format-${t.id}`}
            onClick={() => apply(t.id)} className="rounded p-1.5 text-white/75 hover:bg-white/10 hover:text-white">
            <t.icon className="h-4 w-4" />
          </button>
        ))}
    </div>
  );
}
